import type { AppConfig } from './config';
import type {
  BoxCreateRequest,
  BoxCreateResponse,
  BoxEventItem,
  BoxGetResponse,
  BoxListItem,
  BoxPasswordResponse,
  BoxStackItem,
  Page,
  SshCreateRequest,
  SshKeyItem,
  StackCreateRequest,
  StackListItem,
  StackManifest,
} from './types';

/** The backend returns plain strings for errors rather than structured codes,
 *  so the message is whatever the Lambda wrote. */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Must not exceed the backend cap of 50 (rust_code/shared/src/lib.rs:9). */
export const PAGE_SIZE = 20;

type Query = Record<string, string | number | boolean | undefined | null>;

export class ApiClient {
  readonly #baseUrl: string;
  readonly #token: () => Promise<string>;

  constructor(config: AppConfig, token: () => Promise<string>) {
    this.#baseUrl = config.apiBaseUrl;
    this.#token = token;
  }

  async #request<T>(method: 'GET' | 'POST', path: string, options: { query?: Query; body?: unknown } = {}): Promise<T> {
    const url = new URL(this.#baseUrl + path);
    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }

    const headers: Record<string, string> = { authorization: `Bearer ${await this.#token()}` };
    if (options.body !== undefined) headers['content-type'] = 'application/json';

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
      });
    } catch {
      // fetch only rejects for network-level failures: offline, DNS, TLS, or a
      // preflight the browser refused. The response itself is never the cause.
      throw new ApiError(0, 'The workbench could not be reached. Check your connection and try again.');
    }

    const text = await response.text();
    if (!response.ok) {
      throw new ApiError(response.status, text.trim() || `HTTP ${response.status}`);
    }
    return (text ? JSON.parse(text) : undefined) as T;
  }

  listBoxes(uid: string, options: { ec2Status?: boolean; next?: string | null } = {}): Promise<Page<BoxListItem>> {
    return this.#request('GET', `/v1/user/${encodeURIComponent(uid)}/box/list`, {
      query: { ec2_status: options.ec2Status, next: options.next, limit: PAGE_SIZE },
    });
  }

  createBox(uid: string, request: BoxCreateRequest): Promise<BoxCreateResponse> {
    return this.#request('POST', `/v1/user/${encodeURIComponent(uid)}/box/create`, { body: request });
  }

  getBox(uid: string, boxId: string): Promise<BoxGetResponse> {
    return this.#request('GET', `/v1/user/${encodeURIComponent(uid)}/box/${encodeURIComponent(boxId)}/get`);
  }

  getBoxPassword(uid: string, boxId: string): Promise<BoxPasswordResponse> {
    return this.#request('GET', `/v1/user/${encodeURIComponent(uid)}/box/${encodeURIComponent(boxId)}/password/get`);
  }

  destroyBox(uid: string, boxId: string): Promise<void> {
    return this.#request('POST', `/v1/user/${encodeURIComponent(uid)}/box/${encodeURIComponent(boxId)}/destroy`);
  }

  listBoxStacks(uid: string, boxId: string, next?: string | null): Promise<Page<BoxStackItem>> {
    return this.#request('GET', `/v1/user/${encodeURIComponent(uid)}/box/${encodeURIComponent(boxId)}/stack/get`, {
      query: { next, limit: PAGE_SIZE },
    });
  }

  /** Newest first (rust_code/shared/src/box_mng/db.rs:493 queries descending). */
  listBoxEvents(uid: string, boxId: string, next?: string | null): Promise<Page<BoxEventItem>> {
    return this.#request('GET', `/v1/user/${encodeURIComponent(uid)}/box/${encodeURIComponent(boxId)}/event/get`, {
      query: { next, limit: PAGE_SIZE },
    });
  }

  /** Returns as soon as the job is queued; the box goes Updating until it
   *  finishes, and a failure only shows up as an Error event. */
  deployStacks(uid: string, boxId: string, stackIds: string[]): Promise<void> {
    return this.#request('POST', `/v1/user/${encodeURIComponent(uid)}/box/${encodeURIComponent(boxId)}/stack/deploy`, {
      body: { stack_ids: stackIds },
    });
  }

  listSshKeys(uid: string, next?: string | null): Promise<Page<SshKeyItem>> {
    return this.#request('GET', `/v1/user/${encodeURIComponent(uid)}/ssh/get`, {
      query: { next, limit: PAGE_SIZE },
    });
  }

  createSshKey(uid: string, request: SshCreateRequest): Promise<void> {
    return this.#request('POST', `/v1/user/${encodeURIComponent(uid)}/ssh/create`, { body: request });
  }

  /** `include_non_manifest` also returns prefixes whose manifest is missing or
   *  unreadable; they are skipped server-side otherwise. */
  listStacks(next?: string | null, includeNonManifest = false): Promise<Page<StackListItem>> {
    return this.#request('GET', '/v1/stack/list', {
      query: { next, limit: PAGE_SIZE, include_non_manifest: includeNonManifest || undefined },
    });
  }

  getStack(slug: string): Promise<StackManifest> {
    return this.#request('GET', `/v1/stack/get/${encodeURIComponent(slug)}`);
  }

  createStack(request: StackCreateRequest): Promise<void> {
    return this.#request('POST', '/v1/stack/create', { body: request });
  }
}
